import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Divider } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TestByDay } from '@/services/api/dashboardService';

interface TestsTimelineChartProps {
  data: TestByDay[];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    fill: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const total = payload.reduce((sum, p) => sum + p.value, 0);

  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 0.5 }} />
      {payload.map((p) => (
        <Typography key={p.name} variant="body2" sx={{ color: p.fill }}>
          {p.name}: {p.value}
        </Typography>
      ))}
      <Divider sx={{ my: 0.5 }} />
      <Typography variant="body2" fontWeight="bold">
        Total: {total}
      </Typography>
    </Paper>
  );
}

export function TestsTimelineChart({ data }: TestsTimelineChartProps) {
  const navigate = useNavigate();

  // Handle undefined data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No chart data available
      </Typography>
    );
  }

  // Prepare data for stacked bar chart
  const chartData = data.map((day) => ({
    date: formatDate(day.date),
    dateISO: day.date,
    Sampled: day.sampled,
    Failed: day.failed,
    Cancelled: day.cancelled,
    total: day.count,
  }));

  // Use chart click handler for navigation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChartClick = (state: any) => {
    if (state?.activePayload?.[0]?.payload?.dateISO) {
      const dateISO = state.activePayload[0].payload.dateISO;
      navigate(`/tests?dateFrom=${dateISO}&dateTo=${dateISO}`);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        onClick={handleChartClick}
        style={{ cursor: 'pointer' }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12 }}
          interval="preserveStartEnd"
        />
        <YAxis
          label={{ value: 'Tests', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="Sampled"
          stackId="a"
          fill="#4caf50"
        />
        <Bar
          dataKey="Failed"
          stackId="a"
          fill="#f44336"
        />
        <Bar
          dataKey="Cancelled"
          stackId="a"
          fill="#ff9800"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
